import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useContext } from "react";
import Header from "../components/Header";
import { WalletContext } from "../context/WalletContext";

const API_URL_PAYMENT = "https://bitspend-api.onrender.com/api/transactions/authorize";

export default function EnterPin() {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get merchant wallet from context
  const { selectedWallet } = useContext(WalletContext);
  
  // Get data passed from previous page (EnterAmount)
  const { amount, cardId, share2, merchantAddress } = location.state || {};

  useEffect(() => {
    // Redirect back if no data
    if (!amount || !cardId || !share2) {
      alert("Missing payment data. Please start over.");
      navigate("/");
      return;
    }

    // Use merchantAddress from state, or fallback to context
    if (!merchantAddress && !selectedWallet) {
      alert("Merchant wallet not connected.");
      navigate("/");
    }
  }, [amount, cardId, share2, merchantAddress, selectedWallet, navigate]);

  const handleChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 4) value = value.slice(0, 4);
    setPin(value);
  };

  const handleSubmit = async () => {
    if (pin.length !== 4) {
      alert("Please enter a 4-digit PIN");
      return;
    }

    setLoading(true);

    try {
      // Use merchantAddress from navigation state or selectedWallet from context
      const finalMerchantAddress = merchantAddress || selectedWallet;

      const body = {
        cardId,
        share2,
        pin,
        amount: parseFloat(amount), // Convert to number
        merchantAddress: finalMerchantAddress,
        merchantId: `MERCHANT_${Date.now()}`,
      };

      console.log("Sending payment request:", {
        cardId,
        amount,
        merchantAddress: finalMerchantAddress,
        share2Length: share2.length,
      });

      const res = await fetch(API_URL_PAYMENT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      console.log("Payment response:", data);

      if (res.ok) {
        navigate("/success", { 
          state: { 
            data, 
            amount,
            merchantAddress: finalMerchantAddress,
            cardId 
          } 
        });
      } else {
        navigate("/failed", { 
          state: { 
            error: data.error || data.message || "Transaction failed",
            amount,
            cardId
          } 
        });
      }
    } catch (err) {
      console.error("Transaction failed:", err);
      navigate("/failed", { 
        state: { 
          error: err.message || "Network error",
          amount,
          cardId
        } 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && pin.length === 4) {
      handleSubmit();
    }
  };

  const truncateAddress = (addr) => {
    if (!addr) return "****";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const truncateCardId = (id) => {
    if (!id) return "****";
    if (id.length <= 15) return id;
    return `${id.slice(0, 8)}...${id.slice(-4)}`;
  };

  return (
    <div className="p-6 min-h-screen flex flex-col bg-gradient-to-b from-gray-900 to-gray-800 text-white">
      <Header />
      
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        {/* Payment Summary */}
        <div className="mb-8 w-full max-w-sm">
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 mb-6">
            {/* Card Info */}
            <div className="mb-3 pb-3 border-b border-gray-700">
              <p className="text-gray-400 text-xs mb-1">Payment From Card</p>
              <p className="text-white font-mono text-sm">
                💳 {truncateCardId(cardId)}
              </p>
            </div>
            
            {/* Merchant Info */}
            <div className="mb-3 pb-3 border-b border-gray-700">
              <p className="text-gray-400 text-xs mb-1">Payment To Merchant</p>
              <p className="text-green-400 font-mono text-sm">
                {truncateAddress(merchantAddress || selectedWallet)}
              </p>
            </div>

            {/* Amount */}
            <div className="text-center">
              <p className="text-gray-400 text-xs mb-2">Amount</p>
              <p className="text-4xl font-bold text-green-400">
                ${parseFloat(amount || 0).toFixed(2)}
              </p>
              <p className="text-gray-400 text-xs mt-1">MUSD</p>
            </div>
          </div>
        </div>

        {/* PIN Input */}
        <h1 className="text-xl font-medium mb-6 opacity-80">Enter PIN to Confirm</h1>
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          value={pin}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          maxLength={4}
          placeholder="••••"
          autoFocus
          className="
            text-5xl font-bold text-white bg-gray-800 w-full max-w-sm py-4 text-center rounded-xl
            focus:outline-none focus:ring-2 focus:ring-blue-500
          "
        />

        {/* PIN Dots Indicator */}
        <div className="flex gap-3 mt-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full transition-all duration-200 ${
                pin.length >= i ? "bg-blue-500 scale-110" : "bg-gray-600"
              }`}
            />
          ))}
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={loading || pin.length !== 4}
          className={`mt-8 w-full max-w-sm py-4 rounded-full text-lg font-semibold shadow-lg transition duration-150 
            ${loading || pin.length !== 4
              ? "bg-gray-600 cursor-not-allowed" 
              : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800"}
          `}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Processing Payment...
            </span>
          ) : (
            "Confirm Payment"
          )}
        </button>

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          disabled={loading}
          className="mt-4 text-gray-400 hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ← Back
        </button>

        {/* Security Note */}
        <div className="mt-8 max-w-sm">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <p className="text-yellow-200 text-xs text-center">
              🔒 Enter your 4-digit card PIN to authorize this transaction
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
